// Canonical display formatters for the ESTIMATING side of the app.
//
// Semantics deliberately mirror src/pages/turnover/shared.js (the turnover
// module's canonical fmtDate/fmtCurrency) but are duplicated here rather than
// imported — per the module-boundary rule (CLAUDE.md), estimating code must
// not depend on turnover module code, so a neutral utils file carries the
// same behavior for estimating pages. Turnover-module files should keep
// importing from src/pages/turnover/shared.js instead of this file.

const ET = "America/New_York";

// Parse date-only strings (YYYY-MM-DD) as local midnight so the date never
// flips due to UTC conversion. Full datetime strings are returned as-is (JS
// handles them correctly when a Z or offset suffix is present; without one,
// the value is treated as local — acceptable since the SQL server is Eastern).
export function parseLocalDate(d) {
  if (!d) return null;
  const s = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + "T00:00:00");
  // A SQL DATE column reaches the browser as "2026-10-01T00:00:00.000Z" (the driver makes it UTC midnight). Read in
  // Eastern that is the evening BEFORE, so every quote date showed one day early (feedback #8). A value at exactly
  // UTC midnight is a date, not a moment: take its calendar day as written.
  const m = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.0+)?Z$/.exec(s);
  if (m) return new Date(m[1] + "T00:00:00");
  return new Date(s);
}

// "Aug 28, 2026" — em-dash on missing/invalid input.
export function fmtDate(d) {
  // A calendar date (with or without the driver's UTC-midnight suffix) is printed as that day in any time zone.
  const day = /^(\d{4}-\d{2}-\d{2})(?:T00:00:00(?:\.0+)?Z)?$/.exec(String(d ?? ""));
  if (day) {
    const u = new Date(day[1] + "T00:00:00Z");
    if (!isNaN(u)) return u.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
  }
  const dt = parseLocalDate(d);
  if (!dt || isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-US", { timeZone: ET, month: "short", day: "numeric", year: "numeric" });
}

// "$1,234,567" (whole dollars) — null on falsy input, raw value if non-numeric.
// Same semantics as turnover/shared.js fmtCurrency.
export function fmtCurrency(v) {
  if (!v) return null;
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

// "$1,234.56" (USD, cents) — em-dash on missing/non-numeric input.
// The estimating-side money style (line items, reference pricing).
export function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}
